import Close from "@mui/icons-material/Close";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Typography } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import _ from "lodash";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { type HospitalDataContextType, useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRecommendationsDrawerContext } from "@/contexts/RecommendationsDrawerContext";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useSelfScheduling } from "@/contexts/SelfScheduling/SelfSchedulingContext";
import { useSidePanelHost } from "@/contexts/sidePanel/SidePanelHostContext";
import type { SidePanelHost } from "@/contexts/sidePanel/sidePanelTypes";
import { useWeekIndex } from "@/contexts/WeekIndexProvider";
import { useContractSectionsFilter } from "@/hooks/useContractSectionsFilter";
import { useDepartmentNurseTypes } from "@/hooks/useDepartmentNurseTypes";
import { useLastNameFirst } from "@/hooks/useNameFormat";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useScheduleCacheEnabled } from "@/hooks/useScheduleWeekRules";
import { useShownWeek } from "@/hooks/useShownWeek";
import { localStorageService } from "@/services/LocalStorageService";
import { INK, STATUS, STATUS_BADGE } from "@/theme/tokens";
import type { SystemAutoCompleteOption } from "@/ui/Inputs/SystemAutoComplete/SystemAutoComplete";
import { trackEvent } from "@/utils/analytics";
import { getStaffingIssues, type StaffingIssue, withoutSwapSuggestions } from "@/utils/autoStaffing";
import { scheduleSlotDay } from "@/utils/departmentWeeks";
import { DAYS_PER_WEEK, IssuePriority, RecommendationOpenSource } from "@/utils/models";
import { HeaderButton } from "../headerButton/HeaderButton";
import { DRAWER_WIDTH_IN_PX } from "../RecommendationsDrawer/utils";
import { StaffingHeader } from "./Header/StaffingHeader";
import { useWeekEngineSchedule } from "./hooks/useWeekEngineSchedule";
import { StaffScheduleVisibilityPopup } from "./Settings/StaffScheduleVisibilityPopup";
import Style from "./Staffing.module.scss";
import { WeeklySchedule } from "./WeekSchedule/WeekSchedule";
import { WeekScheduleLoadState } from "./WeekSchedule/WeekScheduleLoadState";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
	open?: boolean;
}>(({ theme, open }) => ({
	height: "100%",
	transition: theme.transitions.create(["margin", "width"], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginRight: `${DRAWER_WIDTH_IN_PX}px`,
		width: `calc(100% - ${DRAWER_WIDTH_IN_PX}px)`,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

export const Staffingv2: FC = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { weekStart } = useShownWeek();
	const isCacheEnabled = useScheduleCacheEnabled();
	const {
		schedule: engineSchedule,
		weekIndex: engineWeekIndex,
		startingDate: engineStartingDate,
		suggestionsLoad: engineSuggestionsLoad,
	} = useWeekEngineSchedule();
	const {
		hospitalData,
		chosenDepartment,
		implementedChanges,
		setImplementedChanges,
		isSnf,
		handleSuggestion,
		isAutoScheduler,
		timezone,
		isHospital,
		calculateShiftTimes,
		// Only hospital demos (Hospital type + inhouse2).
		isHospitalView: isHospitalDemoView,
	}: HospitalDataContextType = useHospitalData();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);

	const { staffRequests } = useRequestsContext();
	const location = useLocation();
	const navigate = useNavigate();
	const { isOpen: open, toggleDrawerOpen } = useRecommendationsDrawerContext();
	const { isDataInSelfSchedulingPeriod } = useSelfScheduling();
	const isBlockRecommendationsV2Enabled = useFeatureFlag(FEATURE_FLAGS.BlockRecommendationsV2);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const lastNameFirst = useLastNameFirst();
	const prevRecommendationsRef = useRef<Array<StaffingIssue>>([]);
	const [isApproveAll, setApproveAll] = useState<boolean>(false);
	const [chosenLevel, setChosenLevel] = useState<IssuePriority>(IssuePriority.TopPriority);
	const [recommendations, setRecommendations] = useState<Array<StaffingIssue>>([]);
	const [searchText, setSearchText] = useState<SystemAutoCompleteOption<string> | null>(null);
	const { selectedContractSections, handleContractSectionsChange } = useContractSectionsFilter({
		getStored: localStorageService.getWeeklyGapsContractSections,
		setStored: localStorageService.setWeeklyGapsContractSections,
		analyticsEvent: "weekly_gaps_filter_contract",
	});

	const firstDate = hospitalData?.weeks[0]?.weeklyShifts[0]?.days[0]?.date;
	const startingDate = firstDate ? firstDate : DateTime.now();
	const endingDate = hospitalData?.weeks[hospitalData.weeks.length - 1]?.weeklyShifts[0]?.days[6]?.date || DateTime.now();
	const [isRequestedTab, setIsRequestedTab] = useState<boolean>(false);
	const [availabilityLoaded, setAvailabilityLoaded] = useState<boolean>(false);
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [autoSchedulerCounterOldVersion, setAutoSchedulerCounterOldVersion] = useState<number>(0);
	const [staffScheduleVisibilityOpen, setStaffScheduleVisibilityOpen] = useState(false);
	const [showStaffVisibilityBanner, setShowStaffVisibilityBanner] = useState<boolean>(true);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const openNotifications = searchParams.get("openNotifications");
		const requestId = searchParams.get("requestId");

		if (openNotifications === "true") {
			toggleDrawerOpen(true, RecommendationOpenSource.WeeklyView);
			setIsRequestedTab(true);
			setChosenLevel(IssuePriority.Optional);
			// Remove the query parameter after opening the drawer
			navigate(location.pathname, { replace: true });
		} else if (requestId) {
			toggleDrawerOpen(true, RecommendationOpenSource.WeeklyView, { tab: "requests", requestId });
			setIsRequestedTab(true);
			setChosenLevel(IssuePriority.Optional);
			// Remove the query parameter after opening the drawer
			const newSearchParams = new URLSearchParams(location.search);
			newSearchParams.delete("requestId");
			const newSearch = newSearchParams.toString();
			navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
		}
	}, [location, navigate, toggleDrawerOpen]);

	useEffect(() => {
		if (!staffRequests || (isHospital && !availabilityLoaded && !isAutoScheduler)) {
			return;
		}

		if (!isBlockRecommendationsV2Enabled) {
			return;
		}

		// The engine reads the weeks either side of the shown one too, which the grid shows before.
		if (engineWeekIndex === -1 || engineSuggestionsLoad.isLoading) {
			prevRecommendationsRef.current = [];
			setRecommendations([]);
			return;
		}

		if (engineSchedule.nurses.length) {
			const issues = getStaffingIssues({
				hospitalData: engineSchedule,
				weekIndex: engineWeekIndex,
				isSnf,
				isAutoScheduler,
				chosenDepartment,
				timezone,
				departmentNurseTypes,
				requests: staffRequests,
				calculateShiftTimes,
				jobManagementEnabled,
				lastNameFirst,
			});
			const newRecommendations = isCacheEnabled ? withoutSwapSuggestions(issues) : issues;
			if (!_.isEqual(newRecommendations, prevRecommendationsRef.current)) {
				prevRecommendationsRef.current = newRecommendations;
				setRecommendations(newRecommendations);
			}
		}
	}, [
		engineSchedule,
		engineWeekIndex,
		engineSuggestionsLoad.isLoading,
		chosenDepartment,
		availabilityLoaded,
		isSnf,
		staffRequests,
		departmentNurseTypes,
		isHospital,
		timezone,
		isAutoScheduler,
		jobManagementEnabled,
		lastNameFirst,
		isCacheEnabled,
	]);

	useEffect(() => {
		if (!isApproveAll) {
			return;
		}

		if (recommendations.some((r) => r.priority === IssuePriority.TopPriority && r.suggestions.length > 0)) {
			new Promise((resolve) => setTimeout(resolve, 100)).then(() => {
				const rec = recommendations.find((r) => r.priority === IssuePriority.TopPriority && r.suggestions.length > 0);

				if (!rec) return;

				const suggestion = rec.suggestions[0];
				const changeKey = `${rec.title}-${suggestion.action}-${engineWeekIndex}`;
				const shiftNo = suggestion.shiftNo;
				const dayNo = suggestion.dayNo;

				trackEvent("auto_staffing_suggestion_implemented", {
					title: rec.title,
					action: suggestion.action,
					week_index: weekIndex,
					shift_no: shiftNo,
					day_no: dayNo,
					priority: rec.priority,
				});

				const { date, shiftType } = scheduleSlotDay(engineSchedule.weeks, { weekIndex: engineWeekIndex, shiftIndex: shiftNo, dayIndex: dayNo });
				handleSuggestion(suggestion, date, shiftType);
				setImplementedChanges((prev) => ({ ...prev, [changeKey]: true }));

				trackEvent("schedule_change_implemented");
			});
		} else {
			// Stepping through every week moves the old list's position; with the cache on only the shown week is fixed.
			if (isAutoScheduler && !isCacheEnabled) {
				if (autoSchedulerCounterOldVersion < maxWeeks) {
					const nextWeekIndex = (weekIndex + 1) % (maxWeeks + 1);

					setWeekIndex(nextWeekIndex);

					setRecommendations(
						getStaffingIssues({
							hospitalData,
							weekIndex: nextWeekIndex,
							isSnf,
							isAutoScheduler: true,
							chosenDepartment,
							timezone,
							departmentNurseTypes,
							requests: staffRequests,
							calculateShiftTimes,
							jobManagementEnabled,
							lastNameFirst,
						}),
					);
					setAutoSchedulerCounterOldVersion((prevCounter) => prevCounter + 1);
				} else {
					setApproveAll(false);
					setAutoSchedulerCounterOldVersion(0); // Reset the counter
				}
			} else {
				setApproveAll(false);
			}
		}
	}, [
		isApproveAll,
		recommendations,
		weekIndex,
		engineSchedule,
		engineWeekIndex,
		departmentNurseTypes,
		isSnf,
		timezone,
		hospitalData,
		isAutoScheduler,
		autoSchedulerCounterOldVersion,
		handleSuggestion,
		chosenDepartment,
		setWeekIndex,
		setImplementedChanges,
		staffRequests,
		jobManagementEnabled,
		lastNameFirst,
		isCacheEnabled,
	]);

	const handleOpenDialog = useCallback((): void => {
		setDialogOpen(true);
		trackEvent("approve_all_dialog_opened");
	}, []);

	const handleCloseDialog = (): void => {
		setDialogOpen(false);
		trackEvent("approve_all_dialog_cancelled");
	};

	// add 1 day to include ending date and remove week before
	const maxWeeks: number = endingDate.plus({ days: 1 }).diff(startingDate, "week").weeks - 1;

	const handleApproveAll = (): void => {
		handleCloseDialog();
		trackEvent("Staffing - auto-staffing button clicked", {
			week_index: weekIndex,
			date_range: `${weekStart?.toFormat("yyyy-MM-dd")} to ${weekStart?.plus({ days: DAYS_PER_WEEK - 1 }).toFormat("yyyy-MM-dd")}`,
			auto_scheduler_version: "old",
			is_auto_scheduler: isAutoScheduler,
		});

		setApproveAll(true);
	};

	const handleDrawerOpen = (): void => {
		trackEvent("copilot_button_clicked", {
			week_index: weekIndex,
			recommendation_count: recommendations.length,
		});
		toggleDrawerOpen(true, RecommendationOpenSource.WeeklyView);
		trackEvent("recommendations_drawer_opened", {
			week_index: weekIndex,
			week_start_date: weekStart?.toFormat("yyyy-MM-dd"),
			recommendations_count: recommendations.length,
		});
	};

	const handleDrawerClose = useCallback((): void => {
		toggleDrawerOpen(false, RecommendationOpenSource.WeeklyView);
		trackEvent("recommendations_drawer_closed", {
			week_index: weekIndex,
			tab: isRequestedTab ? "requested" : "recommendations",
		});
	}, [toggleDrawerOpen, weekIndex, isRequestedTab]);

	const handleRemoveRecommendation = useCallback((index: number): void => {
		setRecommendations((prevRecommendations) => prevRecommendations.filter((_, i) => i !== index));
	}, []);

	// The admin side panel docks beside this grid (which makes room for it) and lists these recommendations.
	const sidePanelHost = useMemo<SidePanelHost>(
		() => ({
			recommendations: {
				recommendations,
				// The engine's own weeks: the recommendations' positions point into them.
				schedule: engineSchedule,
				suggestionsLoad: engineSuggestionsLoad,
				isAutoScheduler,
				weekIndex: engineWeekIndex,
				startingDate: engineStartingDate,
				implementedChanges,
				setImplementedChanges,
				setChosenLevel,
				handleRemoveRecommendation,
				handleOpenDialog,
			},
			onClose: handleDrawerClose,
		}),
		[
			recommendations,
			engineSchedule,
			engineSuggestionsLoad,
			isAutoScheduler,
			engineWeekIndex,
			engineStartingDate,
			implementedChanges,
			setImplementedChanges,
			handleRemoveRecommendation,
			handleOpenDialog,
			handleDrawerClose,
		],
	);
	useSidePanelHost(sidePanelHost);

	// The week's last day is past what staff can see.
	const weekStartIso = weekStart?.toFormat("yyyy-MM-dd");
	const isWeekNotVisibleToStaff = useMemo<boolean>(() => {
		if (!chosenDepartment.staffScheduleDisplayUntilDate || !weekStartIso) {
			return false;
		}
		const lastDay = DateTime.fromISO(weekStartIso, { zone: timezone }).plus({ days: DAYS_PER_WEEK - 1 });
		return lastDay > chosenDepartment.staffScheduleDisplayUntilDate.startOf("day");
	}, [chosenDepartment.staffScheduleDisplayUntilDate, weekStartIso, timezone]);

	useEffect(() => {
		setShowStaffVisibilityBanner(true);
	}, [weekStartIso, chosenDepartment.staffScheduleDisplayUntilDate]);

	return (
		<div
			data-testid={"staffing-v2-root"}
			data-hospital-demo={isHospitalDemoView ? "true" : undefined}
			className={Style.staffContainer}
			style={{
				backgroundColor: weekStart && isDataInSelfSchedulingPeriod(weekStart) ? STATUS_BADGE.WARN_BG : alpha(theme.palette.primary.light, 0.08),
				position: "relative",
				overflow: "auto",
			}}
		>
			<StaffingHeader
				drawerOpen={open}
				weekIndex={weekIndex}
				setSearchText={setSearchText}
				searchText={searchText}
				handleDrawerOpen={handleDrawerOpen}
				setAvailabilityLoaded={setAvailabilityLoaded}
				onStaffScheduleVisibilityClick={() => setStaffScheduleVisibilityOpen(true)}
				selectedContractSections={selectedContractSections}
				onContractSectionsChange={handleContractSectionsChange}
			/>
			{(isCacheEnabled || hospitalData.nurses.length > 0) && (
				<Main open={open} sx={{ marginTop: "90px", minHeight: 0 }}>
					<WeekScheduleLoadState waitsForStaffList={false}>
						<WeeklySchedule
							recommendations={recommendations}
							searchText={searchText?.value || null}
							onSetSearch={(name) => setSearchText({ label: name, value: name })}
							selectedContractSections={selectedContractSections}
						/>
					</WeekScheduleLoadState>
				</Main>
			)}

			<Dialog
				data-testid="staffing-v2-approve-all-dialog"
				open={dialogOpen}
				onClose={() => {
					handleCloseDialog();
					trackEvent("approve_all_dialog_closed_by_backdrop");
				}}
			>
				<DialogTitle>{t("accept.all.changes.confirm")}</DialogTitle>
				<DialogContent>
					<DialogContentText>{t("approve.all.changes.description")}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<HeaderButton
						dataTestId="staffing-v2-approve-all-cancel-button"
						secondary={true}
						disabled={false}
						onPress={() => {
							handleCloseDialog();
							trackEvent("approve_all_cancelled");
						}}
						state="off"
						text="Cancel"
					/>

					<HeaderButton
						dataTestId="staffing-v2-approve-all-confirm-button"
						disabled={false}
						onPress={() => {
							handleApproveAll();
							trackEvent("approve_all_confirmed");
						}}
						state="on"
						text="Approve"
					/>
				</DialogActions>
			</Dialog>

			<StaffScheduleVisibilityPopup
				open={staffScheduleVisibilityOpen}
				onClose={() => setStaffScheduleVisibilityOpen(false)}
				departmentId={chosenDepartment?.department_id || ""}
				currentDate={chosenDepartment?.staffScheduleDisplayUntilDate || null}
				scheduleCycleWeeks={chosenDepartment?.scheduleCycleWeeks}
				schedulePublishLeadWeeks={chosenDepartment?.schedulePublishLeadWeeks}
			/>

			{isWeekNotVisibleToStaff && showStaffVisibilityBanner && (
				<Alert
					severity="warning"
					icon={<InfoOutlined />}
					action={
						<IconButton
							data-testid="staffing-v2-dismiss-visibility-banner"
							size="small"
							onClick={() => setShowStaffVisibilityBanner(false)}
							sx={{ color: STATUS.WARNING.dark, "&:hover": { backgroundColor: alpha(STATUS.WARNING.dark, 0.08) } }}
						>
							<Close fontSize="small" />
						</IconButton>
					}
					sx={{
						position: "fixed",
						bottom: 0,
						left: 0,
						right: open ? `${DRAWER_WIDTH_IN_PX}px` : 0,
						borderRadius: 2,
						zIndex: 1301,
						backgroundColor: STATUS.WARNING.light,
						color: STATUS.WARNING.dark,
						border: `1px solid ${STATUS_BADGE.ORANGE}`,
						transition: "right 0.3s ease",
						"& .MuiAlert-message": { display: "flex", alignItems: "center", width: "100%" },
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<Typography sx={{ fontWeight: 600, color: INK.SECONDARY }}>{t("staff.schedule.visibility.attention")}</Typography>
						<Typography sx={{ color: INK.SECONDARY }}>{t("staff.schedule.visibility.message")}</Typography>
					</Box>
				</Alert>
			)}
		</div>
	);
};

export default Staffingv2;
